import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Tooltip, Tag, Row, Col } from 'antd';
import { AiFillDelete, AiFillEdit } from 'react-icons/ai';
import { MdArrowUpward, MdArrowDownward, MdOutlineVideoLibrary } from 'react-icons/md';
import { toast, ToastContainer } from 'react-toastify';
import { Container } from 'react-bootstrap';
import { BreathAPIs } from '../../APIs/BreathAPIs';
import UserGuidePageForm from './UserGuidePageForm';
import { GUIDE_ICONS, GuideIcon, GuideBody } from './guideIcons';
import styles from './styles.module.css';

const UserGuide = () => {
    const [isLoading, setIsLoading] = useState(true);
    const [pages, setPages] = useState([]);
    const [addModal, setAddModal] = useState(false);
    const [editPage, setEditPage] = useState(null);
    const [deletePage, setDeletePage] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isReordering, setIsReordering] = useState(false);

    useEffect(() => {
        fetchPages();
    }, []);

    const fetchPages = async () => {
        setIsLoading(true);
        try {
            const res = await BreathAPIs.getUserGuideList();
            setPages(res.data.data || []);
        } catch (error) {
            toast.error('Error fetching guide pages.');
        } finally {
            setIsLoading(false);
        }
    };

    const movePage = async (index, direction) => {
        const target = index + direction;
        if (target < 0 || target >= pages.length) return;
        const reordered = [...pages];
        [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
        setPages(reordered);
        setIsReordering(true);
        try {
            await BreathAPIs.reorderUserGuidePages(reordered.map((p) => p._id));
        } catch (error) {
            toast.error('Error saving order.');
            fetchPages();
        } finally {
            setIsReordering(false);
        }
    };

    const handleDelete = async () => {
        setIsDeleting(true);
        try {
            await BreathAPIs.deleteUserGuidePage(deletePage._id);
            toast.success('Guide page deleted.');
            fetchPages();
        } catch (error) {
            toast.error('Error deleting guide page.');
        } finally {
            setIsDeleting(false);
            setDeletePage(null);
        }
    };

    // Where a slide appears in the app (hidden pages aren't sent), for the preview's step counter
    const visiblePages = pages.filter((p) => p.isActive);
    const editIndex = editPage ? visiblePages.findIndex((p) => p._id === editPage._id) : -1;
    const editPosition = editIndex >= 0
        ? { position: editIndex + 1, total: visiblePages.length }
        : { position: visiblePages.length + 1, total: visiblePages.length + 1 };

    const columns = [
        {
            title: '#',
            key: 'order',
            width: 110,
            render: (_, __, index) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ width: 18 }}>{index + 1}</span>
                    <Button
                        size="small"
                        icon={<MdArrowUpward />}
                        disabled={index === 0 || isReordering}
                        onClick={() => movePage(index, -1)}
                    />
                    <Button
                        size="small"
                        icon={<MdArrowDownward />}
                        disabled={index === pages.length - 1 || isReordering}
                        onClick={() => movePage(index, 1)}
                    />
                </div>
            ),
        },
        {
            title: 'Icon',
            dataIndex: 'icon',
            key: 'icon',
            width: 70,
            align: 'center',
            render: (val, record) => record.imageUrl && record.mediaType === 'video' ? (
                <Tooltip title="Video">
                    <span><MdOutlineVideoLibrary size={24} color="#1677ff" /></span>
                </Tooltip>
            ) : record.imageUrl ? (
                <img src={record.imageUrl} alt="" style={{ width: 32, height: 32, objectFit: 'cover', borderRadius: 6 }} />
            ) : (
                <Tooltip title={GUIDE_ICONS[val]?.label || val}>
                    <span><GuideIcon name={val} size={24} /></span>
                </Tooltip>
            ),
        },
        {
            title: 'Title',
            dataIndex: 'title',
            key: 'title',
            width: 200,
            render: (text, record) => (
                <span className={styles['support-view']} onClick={() => setEditPage(record)}>
                    {text}
                </span>
            ),
        },
        {
            title: 'Description',
            dataIndex: 'body',
            key: 'body',
            render: (val) => <GuideBody text={val} />,
        },
        {
            title: 'Visible',
            dataIndex: 'isActive',
            key: 'isActive',
            width: 90,
            render: (val) => (
                <Tag color={val ? 'success' : 'default'}>{val ? 'Yes' : 'No'}</Tag>
            ),
        },
        {
            title: 'Updated',
            dataIndex: 'updatedAt',
            key: 'updatedAt',
            width: 170,
            render: (val) => val ? new Date(val).toLocaleString() : '—',
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 100,
            fixed: 'right',
            render: (_, record) => (
                <div style={{ display: 'flex', gap: '8px' }}>
                    <Tooltip title="Edit">
                        <Button shape="circle" icon={<AiFillEdit />} onClick={() => setEditPage(record)} />
                    </Tooltip>
                    <Tooltip title="Delete">
                        <Button shape="circle" danger icon={<AiFillDelete />} onClick={() => setDeletePage(record)} />
                    </Tooltip>
                </div>
            ),
        },
    ];

    return (
        <Container>
            <ToastContainer />

            <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
                <Col>
                    <span style={{ color: '#666' }}>
                        Slides shown in the mobile app's user guide, in this order. Hidden pages are not sent to the app.
                    </span>
                </Col>
                <Col>
                    <Button type="primary" onClick={() => setAddModal(true)}>
                        New Page
                    </Button>
                </Col>
            </Row>

            <Table
                loading={isLoading}
                dataSource={pages}
                columns={columns}
                rowKey="_id"
                bordered
                size="middle"
                pagination={false}
                scroll={{ x: 'calc(600px + 50%)' }}
                className={styles['data-list']}
            />

            <UserGuidePageForm
                visible={addModal}
                position={visiblePages.length + 1}
                total={visiblePages.length + 1}
                onClose={() => setAddModal(false)}
                onSuccess={() => { setAddModal(false); fetchPages(); }}
            />

            <UserGuidePageForm
                visible={!!editPage}
                page={editPage}
                {...editPosition}
                onClose={() => setEditPage(null)}
                onSuccess={() => { setEditPage(null); fetchPages(); }}
            />

            <Modal
                title="Confirm Deletion"
                open={!!deletePage}
                onOk={handleDelete}
                onCancel={() => setDeletePage(null)}
                okText="Yes, Delete"
                okButtonProps={{ danger: true }}
                cancelText="Cancel"
                confirmLoading={isDeleting}
            >
                <p>
                    Delete guide page <b>{deletePage?.title}</b>? This cannot be undone.
                </p>
            </Modal>
        </Container>
    );
};

export default UserGuide;
